import "react-image-crop/dist/ReactCrop.css";
import React, { useMemo, useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import { GridGenerator } from "@actinc/dls/components";
import {
  Delete as DeleteIcon,
  DragHorizontalVariant as DragHorizontalVariantIcon,
  Plus as PlusIcon,
} from "@actinc/dls/icons/";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  AlertDialog,
  ControlledFormInput,
  ControlledFormSelect,
} from "../../../../components";

import { useDraggableInPortal } from "../../../../services/hooks";
import { onDragEnd } from "../../../../services/utils";

const GraphicGapMatchInteractionForm = ({
  imageAssets = [],
  index,
  insertInteractionElements,
  interaction,
  readonly,
}) => {
  const namePrefix = `item.interactions[${index}]`;
  // console.debug(
  //   "GraphicGapMatchInteractionForm - index %s, interaction %o",
  //   index,
  //   interaction,
  // );

  const imgRef = useRef(null);

  const renderDraggable = useDraggableInPortal();

  const { control, getValues, watch } = useFormContext();
  const [backgroundFileName] = watch([
    `${namePrefix}.backgroundFileName`,
    `${namePrefix}.correctResponses`,
    `${namePrefix}.elements`,
    `${namePrefix}.targets`,
  ]);

  const {
    append: appendCorrectResponse,
    fields: correctResponses,
    remove: removeCorrectResponse,
    update: updateCorrectResponse,
  } = useFieldArray({
    control,
    name: `${namePrefix}.correctResponses`,
  });
  const {
    append: appendElement,
    fields: elements,
    move: moveElement,
    remove: removeElement,
    update: updateElement,
  } = useFieldArray({
    control,
    name: `${namePrefix}.elements`,
  });
  const {
    append: appendTarget,
    fields: targets,
    remove: removeTarget,
    update: updateTarget,
  } = useFieldArray({
    control,
    name: `${namePrefix}.targets`,
  });

  const defaultCropState = {
    height: "10",
    width: "10",
    x: "0",
    y: "0",
    unit: "px", // Can be 'px' or '%'
  };

  const [alertDialogProps, setAlertDialogProps] = useState({ open: false });
  const [completedCrop, setCompletedCrop] = useState(defaultCropState);
  const [crop, setCrop] = useState();
  const [selectedTarget, setSelectedTarget] = useState(null);

  const imageList = imageAssets.map(({ name }) => ({
    label: name,
    value: name,
  }));

  const selectedBackgroundImage = useMemo(
    () => imageAssets.find(({ name }) => name === backgroundFileName),
    [backgroundFileName],
  );

  const addCorrectResponse = () => {
    appendCorrectResponse({
      elementName: "",
      identifier: `CorrectResponse${correctResponses.length + 1}`,
      targetName: "",
    });
  };
  const addElement = () => {
    appendElement({
      imageUrl: "",
      identifier: `Element${elements.length + 1}`,
      matchMax: 0,
    });
  };
  const addTarget = () => {
    const identifier = `Target${targets.length + 1}`;
    appendTarget({
      correctResponse: "",
      height: 0,
      identifier,
      xCoord: 0,
      matchMax: 0,
      yCoord: 0,
      width: 0,
    });
    insertInteractionElements({
      elements: [identifier],
      interaction,
      type: "Graphic Gap Match",
    });
  };
  const closePositioningDialog = (e) => {
    setAlertDialogProps({
      open: false,
    });
    if (e === false) return; // user clicks Cancel
    const { x: xCoord, y: yCoord, width, height } = completedCrop;
    getValues(`${namePrefix}.targets`).map((target, k) => {
      if (target.identifier === selectedTarget.identifier) {
        updateTarget(k, {
          ...target,
          xCoord,
          yCoord,
          height,
          width,
        });
      }
    });
    setCrop(defaultCropState);
    setCompletedCrop(defaultCropState);
  };
  const deleteElement = (i) => () => {
    let deleted = false;
    [...Array(elements.length)].map((_a, j) => {
      const identifier = `Element${j + 1}`;
      // If we haven't reached the deleted element
      if (!deleted) {
        // If we are deleting the current index
        if (i === j) {
          // Remove this element from correctResponses
          getValues(`${namePrefix}.correctResponses`).map(
            (correctResponse, k) => {
              if (correctResponse.element === identifier) {
                updateCorrectResponse(k, {
                  ...correctResponse,
                  element: "",
                });
              }
            },
          );
          deleted = true;
        }
        return;
      }
      // If we are past the deleted element, we need to change element identifiers and target correctResponse values
      replaceElementIdentifier(identifier)(`Element${j}`);
    });
    removeElement(i);
  };
  const deleteTarget = (i) => () => {
    let deleted = false;
    [...Array(targets.length)].map((_a, j) => {
      const identifier = `Targets${j + 1}`;
      // If we haven't reached the deleted target
      if (!deleted) {
        // If we are deleting the current index
        if (i === j) {
          // Remove this target from correctResponses
          getValues(`${namePrefix}.correctResponses`).map(
            (correctResponse, k) => {
              if (correctResponse.target === identifier) {
                updateCorrectResponse(k, {
                  ...correctResponse,
                  target: "",
                });
              }
            },
          );
          deleted = true;
        }
        return;
      }
      // If we are past the deleted element, we need to change element identifiers and target correctResponse values
      //replaceTargetIdentifier(identifier)(`Target${j}`);
    });
    removeTarget(i);
  };
  const deleteCorrectResponse = (i) => () => {
    let deleted = false;
    [...Array(correctResponses.length)].map((_a, j) => {
      const identifier = `CorrectResponses${j + 1}`;
      //replaceCorrectResponseIdentifier(identifier)(`CorrectResponses${j}`);
    });
    removeCorrectResponse(i);
  };
  const replaceElementIdentifier = (oldIdentifier) => (identifier) => {
    getValues(`${namePrefix}.targets`).map((target, k) => {
      if (target.correctResponse === oldIdentifier) {
        updateTarget(k, {
          ...target,
          correctResponse: identifier,
        });
      }
    });
    getValues(`${namePrefix}.elements`).map((element, k) => {
      if (element.identifier === oldIdentifier) {
        updateElement(k, {
          ...element,
          identifier,
        });
      }
    });
  };

  if (!getValues(`${namePrefix}.identifier`)) return null;

  return (
    <Box
      sx={{
        alignItems: "self-start",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
      }}
    >
      <ControlledFormSelect
        control={control}
        defaultValue={interaction.backgroundFileName}
        fullWidth={true}
        id="background-image"
        label="Background Image"
        name={`${namePrefix}.backgroundFileName`}
        options={imageList}
        readonly={readonly}
        rules={{
          required: false,
        }}
      />
      <Typography variant="h4">Elements</Typography>
      <DragDropContext onDragEnd={onDragEnd(moveElement)}>
        <Droppable droppableId={`${interaction.identifier}-elements`}>
          {(droppableProvided) => (
            <List
              dense={true}
              disablePadding={true}
              ref={droppableProvided.innerRef}
              style={{ width: "100%" }}
              {...droppableProvided.droppableProps}
            >
              {elements.map((element, i) => {
                const { id } = element;
                return (
                  <Draggable
                    draggableId={id}
                    index={i}
                    isDragDisabled={readonly}
                    key={id}
                  >
                    {renderDraggable((draggableProvided) => (
                      <ListItem
                        dense={true}
                        ref={draggableProvided.innerRef}
                        {...draggableProvided.draggableProps}
                      >
                        <ListItemIcon>
                          <Box {...draggableProvided.dragHandleProps}>
                            <DragHorizontalVariantIcon />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          disableTypography={true}
                          primary={
                            <Typography
                              sx={{ paddingBottom: 4 }}
                              variant="subtitle1"
                            >
                              {element.identifier}
                            </Typography>
                          }
                          secondary={
                            <GridGenerator
                              groups={[
                                [
                                  {
                                    children: (
                                      <ControlledFormSelect
                                        control={control}
                                        defaultValue={element.imageUrl}
                                        fullWidth={true}
                                        id={`${namePrefix}.elements[${i}].imageUrl`}
                                        label="Image"
                                        name={`${namePrefix}.elements[${i}].imageUrl`}
                                        options={imageList}
                                        readonly={readonly}
                                        rules={{
                                          required: false,
                                        }}
                                      />
                                    ),
                                    key: `element-${id}-imageUrl`,
                                  },
                                  {
                                    children: (
                                      <ControlledFormInput
                                        control={control}
                                        defaultValue={element.matchMax}
                                        fullWidth={true}
                                        id={`${namePrefix}.elements[${i}].matchMax`}
                                        label="Match Max"
                                        multiline={false}
                                        name={`${namePrefix}.elements[${i}].matchMax`}
                                        readonly={readonly}
                                        rules={{
                                          required: true,
                                          maxLength: 2,
                                          pattern: {
                                            message:
                                              "This field must contain a valid number 0-99",
                                            value: /\b([0-9]|[1-9][0-9])\b/,
                                          },
                                        }}
                                      />
                                    ),
                                    key: `element-${id}-matchMax`,
                                  },
                                ],
                              ]}
                            />
                          }
                          sx={{
                            border: "1px solid gray",
                            borderRadius: 3,
                            padding: 1,
                            width: "100%",
                            "& .MuiGrid-item": { paddingTop: 2 },
                          }}
                        />
                        <ListItemSecondaryAction>
                          {!readonly && (
                            <Tooltip title="Delete Element">
                              <IconButton
                                disabled={elements.length <= 1}
                                edge="end"
                                onClick={deleteElement(i)}
                                size="large"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </Draggable>
                );
              })}
              {droppableProvided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      {!readonly && (
        <Button
          onClick={addElement}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Element
        </Button>
      )}
      <Typography variant="h4">Targets</Typography>
      <List dense={true} disablePadding={true} style={{ width: "100%" }}>
        {targets.map((target, i) => {
          const { id } = target;
          const { height, xCoord, yCoord, width } = watch(
            `${namePrefix}.targets[${i}]`,
          );

          const cropCoords = {
            height,
            width,
            x: xCoord,
            y: yCoord,
            unit: "px",
          };

          return (
            <ListItem dense={true} key={id}>
              <ListItemText
                disableTypography={true}
                primary={
                  <Typography sx={{ paddingBottom: 4 }} variant="subtitle1">
                    {target.identifier}
                  </Typography>
                }
                secondary={
                  <GridGenerator
                    groups={[
                      [
                        {
                          children: (
                            <ControlledFormInput
                              control={control}
                              defaultValue={target.matchMax}
                              fullWidth={true}
                              id={`${namePrefix}.targets[${i}].matchMax`}
                              label="Match Max"
                              multiline={false}
                              name={`${namePrefix}.targets[${i}].matchMax`}
                              readonly={readonly}
                              rules={{
                                required: true,
                                maxLength: 2,
                                pattern: {
                                  message:
                                    "This field must contain a valid number 0-99",
                                  value: /\b([0-9]|[1-9][0-9])\b/,
                                },
                              }}
                            />
                          ),
                          key: `target-${id}-matchMax`,
                        },
                        {
                          children: (
                            <Button
                              //disabled={!backgroundFileName}
                              onClick={(e) => {
                                setAlertDialogProps({
                                  confirmButtonText: "Set Location",
                                  dialogTitle: "Select Element Location",
                                  open: true,
                                  readonly,
                                });
                                setCrop({
                                  ...cropCoords,
                                });
                                setCompletedCrop({
                                  ...cropCoords,
                                });
                                setSelectedTarget(
                                  watch(`${namePrefix}.targets[${i}]`),
                                );
                              }}
                              size="small"
                              sx={{
                                minWidth: 145,
                                marginTop: 3,
                                maxHeight: "40px",
                              }}
                            >
                              POSITION TARGET
                            </Button>
                          ),
                          key: `target-${id}-matchMax`,
                        },
                      ],
                    ]}
                  />
                }
                sx={{
                  border: "1px solid gray",
                  borderRadius: 3,
                  padding: 1,
                  width: "100%",
                  "& .MuiGrid-item": { paddingTop: 2 },
                }}
              />
              <ListItemSecondaryAction>
                {!readonly && (
                  <Tooltip title="Delete Target">
                    <IconButton
                      disabled={targets.length <= 1}
                      edge="end"
                      onClick={deleteTarget(i)}
                      size="large"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
      {alertDialogProps.open && (
        <AlertDialog {...alertDialogProps} onClose={closePositioningDialog}>
          <>
            <ReactCrop
              circularCrop={false}
              crop={crop}
              disabled={readonly}
              locked={false}
              onChange={(crop, _percentageCrop) => setCrop(crop)}
              onComplete={(crop) => setCompletedCrop(crop)}
            >
              <img ref={imgRef} alt="crop" src={selectedBackgroundImage?.src} />
            </ReactCrop>
          </>
        </AlertDialog>
      )}
      {!readonly && (
        <Button
          onClick={addTarget}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Target
        </Button>
      )}
      <Typography variant="h4">Correct Responses</Typography>
      <List dense={true} disablePadding={true} style={{ width: "100%" }}>
        {correctResponses.map((correctResponse, i) => {
          const { id } = correctResponse;
          // console.debug("correctResponse:", correctResponse);
          // console.debug("targets:", targets);
          return (
            <ListItem dense={true} key={id}>
              <ListItemText
                //disableTypography={true}
                primary={
                  <Typography sx={{ paddingBottom: 4 }} variant="subtitle1">
                    {`Correct Response ${i + 1}`}
                  </Typography>
                }
                secondary={
                  <GridGenerator
                    groups={[
                      [
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              defaultValue={`${namePrefix}.correctResponses[${i}].target`}
                              fullWidth={true}
                              id={`${namePrefix}.correctResponses[${i}].target`}
                              label="Target"
                              name={`${namePrefix}.correctResponses[${i}].target`}
                              options={
                                targets?.length
                                  ? targets.map((target) => ({
                                      label: target.identifier,
                                      value: target.identifier,
                                    }))
                                  : []
                              }
                              readonly={readonly}
                              rules={{
                                required: true,
                              }}
                            />
                          ),
                          key: `correctResponse-${id}-target`,
                        },
                        {
                          children: (
                            <ControlledFormSelect
                              control={control}
                              defaultValue={`${namePrefix}.correctResponses[${i}].element`}
                              fullWidth={true}
                              id={`${namePrefix}.correctResponses[${i}].element`}
                              label="Element"
                              name={`${namePrefix}.correctResponses[${i}].element`}
                              options={
                                elements?.length
                                  ? elements.map((element) => ({
                                      label: element.identifier,
                                      value: element.identifier,
                                    }))
                                  : []
                              }
                              readonly={readonly}
                              rules={{
                                required: true,
                              }}
                            />
                          ),
                          key: `correctResponse-${id}-element`,
                        },
                      ],
                    ]}
                  />
                }
                sx={{
                  border: "1px solid gray",
                  borderRadius: 3,
                  padding: 1,
                  width: "100%",
                  "& .MuiGrid-item": { paddingTop: 2 },
                }}
              />
              <ListItemSecondaryAction>
                {!readonly && (
                  <Tooltip title="Delete Correct Response">
                    <IconButton
                      disabled={correctResponses.length <= 1}
                      edge="end"
                      onClick={deleteCorrectResponse(i)}
                      size="large"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
      </List>
      {!readonly && (
        <Button
          onClick={addCorrectResponse}
          size="small"
          startIcon={<PlusIcon />}
          variant="outlined"
        >
          Create Correct Response
        </Button>
      )}
    </Box>
  );
};

export { GraphicGapMatchInteractionForm };
